import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIntegrations } from "@/hooks/useIntegrations";
import { IntegrationCard } from "./IntegrationCard";

const CATEGORIES = [
  "All",
  "Productivity",
  "Developer Tools",
  "Communication",
] as const;

export function IntegrationsPage() {
  const { integrations, loading, disconnect } = useIntegrations();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");

  const filtered = useMemo(() => {
    return integrations.filter((i) => {
      if (category !== "All" && i.category !== category) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          i.name.toLowerCase().includes(q) ||
          i.description.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [integrations, search, category]);

  return (
    <div className="mx-auto w-full max-w-4xl p-6">
      <h2 className="mb-2 text-lg font-semibold text-foreground">
        Integrations
      </h2>
      <p className="mb-6 text-sm text-muted-foreground">
        Connect your tools to Deft. The more you connect, the more Deft can do.
      </p>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search integrations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <Tabs value={category} onValueChange={setCategory} className="mb-6">
        <TabsList>
          {CATEGORIES.map((cat) => (
            <TabsTrigger key={cat} value={cat}>
              {cat}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading integrations...</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">No integrations found.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((integration) => (
            <IntegrationCard
              key={integration.id}
              integration={integration}
              onDisconnect={disconnect}
            />
          ))}
        </div>
      )}
    </div>
  );
}
